import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../components/ToastProvider';
import { api } from '../services/api';
import type { ReferralRewardRuleItem } from '../services/types';
import { notifyApiResult } from '../utils/notify';
import { formatDate } from '../utils/format';
import { useAuth } from '../hooks/useAuth';

const TIERS = ['default', 'premium'] as const;

export function ReferralRulesPage() {
  const { user } = useAuth();
  const toast = useToast();
  const canWrite = user?.role === 'superadmin';

  const [rules, setRules] = useState<ReferralRewardRuleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [buyer, setBuyer] = useState('default');
  const [owner, setOwner] = useState('default');
  const [reward, setReward] = useState('default');
  const [days, setDays] = useState('3');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editReward, setEditReward] = useState('default');
  const [editDays, setEditDays] = useState('3');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.listReferralRules();
    if (res.data) setRules(res.data);
    else if (res.error) {
      setError(res.error.message);
      toast.error(res.error.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const createRule = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const res = await api.upsertReferralRule({
      buyer_subscription_tier: buyer,
      owner_subscription_tier: owner,
      reward_subscription_tier: reward,
      reward_days: Number(days),
      enabled: true,
    });
    if (notifyApiResult(toast, res, () => 'Правило сохранено')) {
      load();
    } else if (res.error) setError(res.error.message);
  };

  const startEdit = (r: ReferralRewardRuleItem) => {
    setEditingId(r.id);
    setEditReward(r.reward_subscription_tier);
    setEditDays(String(r.reward_days));
  };

  const saveEdit = async (r: ReferralRewardRuleItem) => {
    const res = await api.updateReferralRule(r.id, {
      buyer_subscription_tier: r.buyer_subscription_tier,
      owner_subscription_tier: r.owner_subscription_tier,
      reward_subscription_tier: editReward,
      reward_days: Number(editDays),
      enabled: r.enabled,
    });
    if (notifyApiResult(toast, res, () => 'Правило обновлено')) {
      setEditingId(null);
      load();
    } else if (res.error) setError(res.error.message);
  };

  const toggle = async (r: ReferralRewardRuleItem) => {
    const res = await api.setReferralRuleEnabled(r.id, !r.enabled);
    if (notifyApiResult(toast, res, () => (r.enabled ? 'Выключено' : 'Включено'))) {
      load();
    } else if (res.error) setError(res.error.message);
  };

  return (
    <div>
      <div className="page-header">
        <h1>Реферальные правила</h1>
      </div>
      <p style={{ color: 'var(--text-muted)', marginTop: 0, maxWidth: '42rem' }}>
        Матрица начислений: тип купленной подписки друга × текущий тариф реферера → бонус рефереру.
        Меняется без перезапуска API. Удаление правил запрещено — только включение/выключение.
      </p>

      {canWrite && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>Создать / обновить пару tier</h3>
          <form onSubmit={createRule} className="filters-row">
            <select className="select" value={buyer} onChange={(e) => setBuyer(e.target.value)}>
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  Buyer: {t}
                </option>
              ))}
            </select>
            <select className="select" value={owner} onChange={(e) => setOwner(e.target.value)}>
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  Owner: {t}
                </option>
              ))}
            </select>
            <select className="select" value={reward} onChange={(e) => setReward(e.target.value)}>
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  Reward: {t}
                </option>
              ))}
            </select>
            <input
              className="input"
              type="number"
              min={1}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              required
            />
            <button type="submit" className="btn btn-primary">
              Сохранить
            </button>
          </form>
        </div>
      )}

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      {loading && <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>}

      {!loading && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Buyer</th>
                <th>Owner</th>
                <th>Reward tier</th>
                <th>Дней</th>
                <th>Вкл</th>
                <th>Обновлён</th>
                {canWrite && <th></th>}
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 && (
                <tr>
                  <td colSpan={canWrite ? 7 : 6} style={{ color: 'var(--text-muted)' }}>
                    Правил нет
                  </td>
                </tr>
              )}
              {rules.map((r) => {
                const editing = editingId === r.id;
                return (
                  <tr key={r.id}>
                    <td>{r.buyer_subscription_tier}</td>
                    <td>{r.owner_subscription_tier}</td>
                    <td>
                      {editing ? (
                        <select
                          className="select"
                          value={editReward}
                          onChange={(e) => setEditReward(e.target.value)}
                        >
                          {TIERS.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      ) : (
                        r.reward_subscription_tier
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          className="input"
                          type="number"
                          min={1}
                          value={editDays}
                          onChange={(e) => setEditDays(e.target.value)}
                          style={{ width: '5rem' }}
                        />
                      ) : (
                        r.reward_days
                      )}
                    </td>
                    <td>
                      <span className={r.enabled ? 'badge badge-success' : 'badge badge-muted'}>
                        {r.enabled ? 'да' : 'нет'}
                      </span>
                    </td>
                    <td>{formatDate(r.updated_at)}</td>
                    {canWrite && (
                      <td style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                        {editing ? (
                          <>
                            <button type="button" className="btn btn-primary" onClick={() => void saveEdit(r)}>
                              OK
                            </button>
                            <button type="button" className="btn btn-ghost" onClick={() => setEditingId(null)}>
                              Отмена
                            </button>
                          </>
                        ) : (
                          <>
                            <button type="button" className="btn btn-ghost" onClick={() => startEdit(r)}>
                              Изменить
                            </button>
                            <button type="button" className="btn btn-ghost" onClick={() => void toggle(r)}>
                              {r.enabled ? 'Выкл' : 'Вкл'}
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
